import { useNavigate } from '@rocketlane/rocket-router';
import { useBoolean } from 'ahooks';
import { useEffect, useRef, useState } from 'react';
import { AuthenticationServiceProvider } from 'api/account';
import { triggerOtp } from 'components/pages/two-factor-auth/utils';
import { LS_TEMPLATE_ID_KEY, RoutePaths } from 'constant';
import { useAccountMetaQuery } from 'queries';
import { ActiveTab, ApiKey, getOkta } from 'services';
import { redirectToOAuthWithMagicLink } from 'utils/oauth-redirect';
import { getTokenMeta, isLinkMagicToken, PermissionType } from 'utils/token';
import { useCustomAuth } from './use-custom-auth';
import { useQueryParams } from './use-query-params';
// Hard navigation, not SPA navigate — an SPA nav here either no-ops or races auth-state
// propagation, bouncing the protected-route gate back to home (post-#17308).
export const redirectToSavedTab = (_history)=>{
    var _ref, _ref1, _ref2;
    const savedTab = ActiveTab.pop();
    const url = savedTab ? new URL(savedTab) : null;
    const pathname = (_ref = url === null || url === void 0 ? void 0 : url.pathname) !== null && _ref !== void 0 ? _ref : '/';
    const hash = (_ref1 = url === null || url === void 0 ? void 0 : url.hash) !== null && _ref1 !== void 0 ? _ref1 : '';
    // Preserve genuine deep-link query params (e.g. showCsatForm, rating) so the
    // post-login reload restores the intended destination (a customer clicking a
    // CSAT email link on mobile must land on the task, not home). Strip only the
    // auth-consumption params so the reload cannot re-trigger the magic-link flow
    // or leave the token lingering in the address bar.
    if (url) {
        url.searchParams.delete('token');
        url.searchParams.delete('rl-token');
    }
    const search = (_ref2 = url === null || url === void 0 ? void 0 : url.search) !== null && _ref2 !== void 0 ? _ref2 : '';
    window.location.replace(`${pathname}${search}${hash}`);
};
export const useLoginWithToken = (token, tokenType)=>{
    var _params_get, _params_get1, _ref;
    var _data_okta, _data_okta1;
    const history = useNavigate();
    const replace = useNavigate();
    const [isError, setError] = useState(false);
    const [isLoading, { setTrue, setFalse }] = useBoolean(false);
    const params = useQueryParams();
    const externalRefKey = (_params_get = params.get('externalRefKey')) !== null && _params_get !== void 0 ? _params_get : '';
    const projectId = (_params_get1 = params.get('projectId')) !== null && _params_get1 !== void 0 ? _params_get1 : '';
    const { data } = useAccountMetaQuery();
    const isCognitoEnabled = (data === null || data === void 0 ? void 0 : (_data_okta = data.okta) === null || _data_okta === void 0 ? void 0 : _data_okta.authenticationServiceProvider) === AuthenticationServiceProvider.COGNITO;
    const cookiesEnabled = (_ref = data === null || data === void 0 ? void 0 : (_data_okta1 = data.okta) === null || _data_okta1 === void 0 ? void 0 : _data_okta1.cookiesEnabled) !== null && _ref !== void 0 ? _ref : false;
    const clientId = data === null || data === void 0 ? void 0 : data.okta.clientId;
    const { setCustomAuth } = useCustomAuth();
    // Guards against re-fire after post-consume navigation drops token to '' (would clear the
    // just-created api-key and 401 on re-create — see redirectToSavedTab).
    const consumed = useRef(false);
    useEffect(()=>{
        // Don't consume an empty token (happens during post-redirect re-render), and don't
        // re-consume after a successful run (defense-in-depth against future re-fire causes).
        if (!token || consumed.current) {
            return;
        }
        const loginWithoutOtp = async ()=>{
            setTrue();
            if (ApiKey.exists()) {
                await ApiKey.clear();
            }
            if (clientId) {
                consumed.current = true;
                try {
                    var _getOkta;
                    /**
           * Check if magic link contains hash to load the task drawer.
           */ await ApiKey.create(token, tokenType, clientId, externalRefKey, projectId);
                    (_getOkta = getOkta()) === null || _getOkta === void 0 ? void 0 : _getOkta.authStateManager.updateAuthState();
                    setFalse();
                    const templateId = localStorage.getItem(LS_TEMPLATE_ID_KEY);
                    localStorage.removeItem(LS_TEMPLATE_ID_KEY);
                    if (isCognitoEnabled && templateId) {
                        replace(RoutePaths.documentTemplate.replace(':templateId', templateId));
                        return;
                    }
                    redirectToSavedTab(history);
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                } catch (e) {
                    consumed.current = false;
                    setFalse();
                    setError(true);
                }
            }
        };
        const loginWithOtp = async ()=>{
            try {
                /**
         * Check if magic link contains hash to load the task drawer.
         */ if (ApiKey.exists()) {
                    redirectToSavedTab(history);
                } else {
                    consumed.current = true;
                    triggerOtp(token);
                    history(RoutePaths.magicLink2fa, {
                        state: {
                            token
                        }
                    });
                }
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            } catch (e) {
                consumed.current = false;
                setError(true);
            }
        };
        // Stashes LINK_MAGIC and redirects to id PKCE; POST /authentication/token at the callback
        // end atomically validates, runs the link-type action, blacklists, and sets rl_session.
        const loginWithLinkMagic = async ()=>{
            if (!clientId) {
                setError(true);
                return;
            }
            setTrue();
            consumed.current = true;
            try {
                // saveIfEmpty, not save — an upstream capture of the real deep link may already exist.
                ActiveTab.saveIfEmpty();
                // Also stashes LINK_MAGIC in sessionStorage for LoginCallback to consume.
                await redirectToOAuthWithMagicLink(clientId, token);
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            } catch (e) {
                consumed.current = false;
                setFalse();
                setError(true);
            }
        };
        const exec = ()=>{
            const { decoded, isExpired } = getTokenMeta(token);
            const isOTPEnabled = (decoded === null || decoded === void 0 ? void 0 : decoded.permission) === PermissionType.UserOtp || false;
            if (isLinkMagicToken(token) && cookiesEnabled) {
                // Guards a short-TTL race: token may expire between login.tsx's render check and here.
                if (isExpired) {
                    setError(true);
                    return;
                }
                loginWithLinkMagic();
            } else if (isOTPEnabled) {
                loginWithOtp();
            } else {
                loginWithoutOtp();
            }
        };
        exec();
    }, [
        token,
        setTrue,
        setFalse,
        history,
        tokenType,
        externalRefKey,
        projectId,
        clientId,
        isCognitoEnabled,
        cookiesEnabled,
        setCustomAuth,
        replace
    ]);
    return [
        isLoading,
        isError
    ];
};
